// Wallet PCF de un usuario de la cadena: verificar y vincular otra, o desvincular la actual.
import { useState } from 'react';
import { toast } from 'sonner';
import { userMessageOf } from '@/api/errors';
import { Button, ConfirmDialog, Modal } from '@/kit';
import {
    useLinkRetailUserWallet,
    useUnlinkRetailUserWallet,
    type RetailUser,
} from '../api/useRetailOrg';
import { WalletLinkField } from './WalletLinkField';
import { linkedWalletText, localMxPhone, type WalletFieldState } from './walletLink';

interface SellerWalletModalProps {
    retailerId: string;
    user: RetailUser;
    name: string;
    onClose: () => void;
}

export function SellerWalletModal({ retailerId, user, name, onClose }: SellerWalletModalProps) {
    const [wallet, setWallet] = useState<WalletFieldState>({ kind: 'empty' });
    const [confirmUnlink, setConfirmUnlink] = useState(false);
    // Los avisos van en la mutación: corren aunque el modal ya se haya cerrado.
    const link = useLinkRetailUserWallet(retailerId, {
        onSuccess: () => {
            toast.success('Wallet vinculada', {
                description: 'Los pagos que vienen se depositan en esta wallet.',
            });
            onClose();
        },
        onError: (error) => {
            toast.error(userMessageOf(error));
        },
    });
    const unlink = useUnlinkRetailUserWallet(retailerId, {
        onSuccess: () => {
            toast.success('Wallet desvinculada', {
                description: 'Sus comisiones quedan pendientes hasta vincular otra.',
            });
            setConfirmUnlink(false);
            onClose();
        },
        onError: (error) => {
            toast.error(userMessageOf(error));
            setConfirmUnlink(false);
        },
    });
    const busy = link.isPending || unlink.isPending;
    const current = linkedWalletText(user);

    // Con una petición en vuelo el modal no se cierra: su resultado se vería en otra pantalla.
    const close = () => {
        if (!busy) onClose();
    };

    const save = () => {
        if (wallet.kind !== 'verified') return;
        link.mutate({ retailUserId: user.retailUserId, body: wallet.body });
    };

    const remove = () => {
        unlink.mutate(user.retailUserId);
    };

    return (
        <Modal isOpen onClose={close} title={`Wallet PCF · ${name}`} size="sm">
            <div className="flex flex-col gap-4">
                <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[12px]">
                    {current ? (
                        <>
                            <b>Vinculada:</b> <span>{current}</span>
                        </>
                    ) : (
                        <>
                            <b>Sin wallet.</b>{' '}
                            <span>Sus comisiones quedan pendientes hasta vincularla.</span>
                        </>
                    )}
                </p>
                <WalletLinkField
                    retailerId={retailerId}
                    hint="Solo se guardan los últimos 4 dígitos de la CLABE."
                    suggestedPhone={localMxPhone(user.phone ?? '')}
                    disabled={busy}
                    onStateChange={setWallet}
                />
                {current && (
                    <p className="text-[11px] text-muted-foreground">
                        Cambiar la wallet solo afecta los pagos que vienen.
                    </p>
                )}
                <div className="flex justify-end gap-2">
                    {current && (
                        <Button
                            type="button"
                            variant="outline"
                            className="mr-auto text-danger"
                            disabled={busy}
                            onClick={() => {
                                setConfirmUnlink(true);
                            }}
                        >
                            Desvincular
                        </Button>
                    )}
                    <Button type="button" variant="outline" disabled={busy} onClick={close}>
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        disabled={wallet.kind !== 'verified' || unlink.isPending}
                        isLoading={link.isPending}
                        onClick={save}
                    >
                        {current ? 'Cambiar wallet' : 'Vincular'}
                    </Button>
                </div>
            </div>
            {confirmUnlink && (
                <ConfirmDialog
                    open
                    onOpenChange={(open) => {
                        if (!open && !unlink.isPending) setConfirmUnlink(false);
                    }}
                    title="¿Desvincular la wallet?"
                    description="Sus comisiones se siguen calculando y quedan pendientes hasta vincular otra."
                    confirmLabel="Desvincular"
                    destructive
                    loading={unlink.isPending}
                    onConfirm={remove}
                />
            )}
        </Modal>
    );
}
